<template>
  <div class="create-edit-map-wrapper">
    <div v-if="mapLoad" class="preloader minimap-preloader">
      <svg class="loader minimap-loader" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 340">
        <circle cx="170" cy="170" r="160" stroke="#4E53E7"/>
        <circle cx="170" cy="170" r="135" stroke="#404041"/>
        <circle cx="170" cy="170" r="110" stroke="#4E53E7"/>
        <circle cx="170" cy="170" r="85" stroke="#404041"/>
      </svg>
    </div>

    <div class="axis">
      <v-select
        v-model="selectedProjection"
        :options="projections"
        label="title"
        :searchable="false"
      >
        <span slot="no-options">Не найдено совпадений</span>
      </v-select>
      <button class="btn accent-btn" @click="onPaste()">Вставить</button>
      <button class="btn accent-btn" @click="onCopy()">Копировать</button>
    </div>
    <div id="popup-map">
      <div class="layers-popup" v-if="layersPopup.length > 0">
        <span v-for="(lay, index) in layersPopup" :key="index" @click="toggleVisibility(lay)" :class="{'active-layer': isSpBound}">{{ lay.name }}</span>
      </div>
    </div>
    <div class="calculation-box">
      <div class="calculated-area" v-if="roundedArea !== null">
        {{ 'Фактическая площадь: ' + roundedArea.toLocaleString('ru-RU') }} м<sup>2</sup>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import * as turf from '@turf/turf'
import axios from 'axios'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import proj4 from 'proj4'

export default {
  name: 'map-create-edit-component',
  props: ['areaBygeomForMap', 'initCoords', 'createMap', 'mode'],
  data () {
    return {
      map: null,
      isSpBound: false,
      geometryForMap: null,
      flatCoordinates: [],
      geoJsonCoordinates: [],
      layersPopup: [],
      geomTableCoords: [],
      roundedArea: null,
      symbolsHole: ['_', '\n', '\t', ''],
      selectedProjection: {
        proj: '+proj=tmerc +lat_0=0 +lon_0=38.48333333333 +k=1 +x_0=1250000 +y_0=-5212900.566 +ellps=krass +towgs84=23.57,-140.95,-79.8,0,0.35,0.79,-0.22 +units=m +no_defs',
        title: 'МСК-36 зона 1 Воронежская область',
        name: '633601'
      },
      projections: [
        {
          proj: '+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees',
          title: 'epsg4326 (WGS84)',
          name: 'epsg4326'
        },
        {
          proj: '+proj=tmerc +lat_0=0 +lon_0=38.48333333333 +k=1 +x_0=1250000 +y_0=-5212900.566 +ellps=krass +towgs84=23.57,-140.95,-79.8,0,0.35,0.79,-0.22 +units=m +no_defs',
          title: 'МСК-36 зона 1 Воронежская область',
          name: '633601'
        },
        {
          proj: '+proj=tmerc +lat_0=0 +lon_0=41.48333333333 +k=1 +x_0=2250000 +y_0=-5212900.566 +ellps=krass +towgs84=23.57,-140.95,-79.8,0,0.35,0.79,-0.22 +units=m +no_defs',
          title: 'МСК-36 зона 2 Воронежская область',
          name: '633602'
        }
      ],
      mapLoad: false,
      startInit: false,
      draw: null
    }
  },

  watch: {
    createMap (newVal, oldVal) {
      if (newVal && !this.startInit) {
        this.startInit = true
        this.initMap()
      }
    }
  },

  methods: {
    toggleVisibility (layer) {
      this.map.setLayoutProperty(layer.id, 'visibility', layer.visibility ? 'none' : 'visible')
      this.isSpBound = !this.isSpBound
      layer.visibility = !layer.visibility
    },
    transformProjToEPSG4326 (fromSrid, coords) {
      if (fromSrid.name === 'epsg4326') {
        return coords
      }
      var toSrid = '+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees'
      return proj4(fromSrid.proj, toSrid, coords.reverse())
    },

    transformProjFromEPSG4326 (toSrid, coords) {
      if (toSrid.name === 'epsg4326') {
        return coords
      }
      var fromSrid = '+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees'
      // console.log('coords', coords)
      let converted = proj4(fromSrid, toSrid.proj, coords)
      return [converted[1], converted[0]]
    },

    converCoordsFromSrid (fromSrid, coords) {
      let newCoords = []
      for (let i of coords) {
        if (i[0] === '_') {
          newCoords.push(['_', '_'])
        } else {
          if (typeof i[0] === 'string') {
            i[0] = parseFloat(i[0])
            i[1] = parseFloat(i[1])
          }
          newCoords.push(this.transformProjToEPSG4326(fromSrid, i))
          this.transformProjToEPSG4326(fromSrid, i)
        }
      }
      return newCoords
    },

    converCoordsToSrid (toSrid, coords) {
      let newCoords = []
      for (let i of coords) {
        if (i[0] === '_') {
          newCoords.push(['_', '_'])
        } else {
          if (typeof i[0] === 'string') {
            i[0] = parseFloat(i[0])
            i[1] = parseFloat(i[1])
          }
          newCoords.push(this.transformProjFromEPSG4326(toSrid, i))
          this.transformProjFromEPSG4326(toSrid, i)
        }
      }
      return newCoords
    },

    geoJsonCoordsToFlatArray (coordinates) {
      if (!Array.isArray(coordinates)) {
        return
      }
      if (coordinates.length > 2) {
        for (var i of coordinates) {
          return this.geoJsonCoordsToFlatArray(i)
        }
      }
      if (coordinates.length === 2) {
        this.flatCoordinates.push(coordinates)
      }
    },

    flatArrayCoordsToGeoJson (coordinates) {
      // geoJsonCoordinates
      // var currentPolygon = []
      if (!Array.isArray(coordinates)) {
        return
      }
      for (var i of coordinates) {
        if (i.length !== 2) {
          continue
        }
        if (i[0] === 'x' || i[1] === 'y') {
          continue
        }
        if (i[0] === ' ') {
        }
      }
    },

    async onPaste (e) {
      try {
        let text = await navigator.clipboard.readText()
        let _arrText = text.split(/\n+/)
        this.geomTableCoords = [[]]
        let counterContour = 0
        // для того чтобы если начальная и конечная координаты не совпадают
        let rememberCoord = true
        let startCoord = null
        let lastCoord = null
        while (_arrText[_arrText.length - 1] === '\n' || _arrText[_arrText.length - 1] === '') {
          _arrText.splice(-1, 1)
        }
        for (let i = 0; i < _arrText.length; i++) {
          let k = _arrText[i]
          k = k.replaceAll('\t', ' ')
          k = k.trim()
          let kRow = k.split(/\s+/)
          if (this.isNewCountourSymbol(kRow)) {
            if (i > 0) {
              rememberCoord = true // запомнить след координату как первую (будет из нового контура)
              if (startCoord[0] !== lastCoord[0] || startCoord[1] !== lastCoord[1]) { // посмотреть совпадает ли послежняя координата с первой
                this.geomTableCoords[counterContour].push(startCoord)
              }
              this.geomTableCoords.push([])
              counterContour++
            }
            continue
          }
          if (this.isNewHole(kRow)) { // когда послледняя координата контура/дырки нужно сверить совпадает ли первая и последняя точки
            kRow[0] = '_'
            kRow[1] = '_'
            rememberCoord = true
            if (startCoord[0] !== lastCoord[0] || startCoord[1] !== lastCoord[1]) {
              this.geomTableCoords[counterContour].push(startCoord)
            }
          }
          if (rememberCoord && kRow[0] !== '_' && kRow[1] !== '_') { // запомнить первую координату
            startCoord = kRow
            rememberCoord = false
          }
          lastCoord = kRow
          this.geomTableCoords[counterContour].push(kRow)
          if (i === _arrText.length - 1) {
            if (startCoord[0] !== lastCoord[0] || startCoord[1] === lastCoord[1]) {
              this.geomTableCoords[counterContour].push(startCoord)
            }
          }
        }
        this.geometryForMap = this.drawFromTable()
        this.$emit('setCoords', this.geometryForMap)
      } catch (error) {
        console.error(error)
        this.$notify({
          group: 'foo',
          type: 'error',
          title: 'Вставка',
          text: 'Не удалось отобразить геометрию. Возможно, вы пытаетесь отобразить не координаты. Данная функция может не работать в Firefox.'
        })
      }
    },

    isNewCountourSymbol (coords) {
      let coord1 = null
      if (Array.isArray(coords)) {
        coord1 = coords[0]
      } else {
        coord1 = coords
      }
      if (!this.isNewHole(coord1) && isNaN(parseFloat(coord1))) { // если не дырка и не координата
        return true
      } else {
        return false
      }
    },

    isNewHole (coords) {
      return this.symbolsHole.indexOf(coords[0]) !== -1
    },

    onCopy () {
      try {
        let textForCopy = ''
        // console.log('geometryForMap.features', this.geometryForMap.features)
        let layer = this.geometryForMap
        for (let i = 0; i < layer.features.length; i++) {
          textForCopy += '\nxy\n'
          if (layer.features[i].geometry.type === 'MultiPolygon') {
            // console.log('MULTI')
            for (let j = 0; j < layer.features[i].geometry.coordinates.length; j++) {
              for (let ij = 0; ij < layer.features[i].geometry.coordinates[j].length; ij++) {
                layer.features[i].geometry.coordinates[j][ij].forEach(dot => {
                  let convertedDot = this.transformProjFromEPSG4326(this.selectedProjection, dot)
                  textForCopy += String(convertedDot[0]) + '\t' + String(convertedDot[1]) + '\n'
                })
                textForCopy += '_\n'
              }
              // textForCopy += '_\n'
            }
          } else if (layer.features[i].geometry.type === 'Polygon') {
            for (let j = 0; j < layer.features[i].geometry.coordinates.length; j++) {
              layer.features[i].geometry.coordinates[j].forEach(dot => {
                let convertedDot = this.transformProjFromEPSG4326(this.selectedProjection, dot)
                textForCopy += String(convertedDot[0]) + '\t' + String(convertedDot[1]) + '\n'
              })
              textForCopy += '_\n'
            }
          } else {
            console.error('fail conver wgs to mercator')
          }
          textForCopy = textForCopy.slice(0, -3)
        }

        // for (let i = 0; i < this.geometryForMap.features.length; i++) {
        //   let coords = this.geometryForMap.features[i].geometry.coordinates
        //   textForCopy += '\nxy\n'
        //   for (let contour of coords) {
        //     contour.forEach(dot => {
        //       // if (Array.isArray(dot[0])) {
        //       //   dot.forEach(realDot => {
        //       //     let convertedDot = this.transformProjFromEPSG4326(this.selectedProjection, realDot)
        //       //     textForCopy += String(convertedDot[0]) + '\t' + String(convertedDot[1]) + '\n'
        //       //   })
        //       // } else {
        //       //   // console.log('dot', dot)
        //       //   let convertedDot = this.transformProjFromEPSG4326(this.selectedProjection, dot)
        //       //   textForCopy += String(convertedDot[0]) + '\t' + String(convertedDot[1]) + '\n'
        //       // }
        //       // console.log('dot', dot)
        //       let convertedDot = this.transformProjFromEPSG4326(this.selectedProjection, dot)
        //       textForCopy += String(convertedDot[0]) + '\t' + String(convertedDot[1]) + '\n'
        //     })
        //     textForCopy += '_\n'
        //   }
        //   textForCopy = textForCopy.slice(0, -3)
        // }

        textForCopy = textForCopy.slice(1)
        navigator.clipboard.writeText(textForCopy)
        this.$notify({
          group: 'foo',
          type: 'success',
          title: 'Скопировано',
          text: 'Координаты успешно скопированы'
        })
      } catch (error) {
        this.$notify({
          group: 'foo',
          type: 'error',
          title: 'Копирование',
          text: 'Не удалось скопировать координаты'
        })
        console.error('fail copy coords:', error)
      }
    },

    drawFromTable () {
      let features = []

      try {
        for (let i = 0; i < this.geomTableCoords.length; i++) {
          let convertedCoords = this.converCoordsFromSrid(this.selectedProjection, this.geomTableCoords[i])
          features.push({
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [[]]
            }
          })
          let arrIndex = 0
          convertedCoords.forEach(dot => {
            if (dot[0] === '_' || dot[1] === '_') { // если "_", то значит это дырка или мультиполигон
              arrIndex++
              features[i].geometry.coordinates.push([])
            } else { // если не "_"
              features[i].geometry.coordinates[arrIndex].push(dot)
            }
          })
        }
      } catch (error) {
      }

      let geojson = {
        type: 'FeatureCollection',
        features: features
      }

      this.draw.deleteAll()
      features.forEach((feature, i) => {
        feature.id = 'paste_' + i
        this.draw.add(feature)
      })
      let bounds = turf.bbox(geojson) // позиционировани на фичу
      // let bounds = this.getBoundsFromGeometryMix(features[0].geometry) // позиционировани на фичу
      this.map.fitBounds(bounds, {
        padding: 20
      })

      return geojson
    },

    initMap () {
      if (this.isMapReady) return
      setTimeout(() => {
        this.mapLoad = true
        /* eslint-disable */
        this.isMapReady = true
        // var SPRITE = 'https://bg-map.govvrn.ru/sprite';
        let SPRITE = 'mapbox://sprites/mapbox/streets-v8';
        // var GLYPHS = 'https://bg-map.govvrn.ru/glyphs/{fontstack}/{range}.pbf';
        let GLYPHS = 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf';
        let KEY = 'no-token';
        mapboxgl.accessToken = KEY
        this.map = new mapboxgl.Map({
          container: 'popup-map',
          style: {
            version: 8,
            name: 'mapdev.io',
            center: [
                40.5,
                51
            ],
            zoom: 7,
            sprite: 'mapbox://sprites/art-il/ckhhvi70l1ji919odopny5ml7/58scb43jppw60qyifmkeouviw',
            glyphs: GLYPHS,
            sources: {
              osm: {
                type: 'raster',
                tiles: [
                  'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                  'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                  'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
                ],
                tileSize: 256
              },
              satellite: {
                type: 'raster',
                tiles: [
                  'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'
                ],
                tileSize: 256
              },
              test: {
                type: 'vector',
                tiles: [
                  `${this.$store.getters.value('host')}/ugeoapi/api/v1/land/vector_tiles/{z}/{x}/{y}.mvt`
                ],
                promoteId: {
                  "layer": "id"
                }
              }
            },
            layers: [
            {
              "id": "background-map",
              "type": "background",
              "paint": {
                "background-color": "white"
              }
            },
            {
              "id": "openstreetmap",
              "type": "raster",
              "source": "osm",
              "metadata": {
                "mapbox:layer:caption": "OpenStreetMap.org"
              }
            },
            {
              "id": "satellite",
              "type": "raster",
              "source": "satellite",
              "metadata": {
                "mapbox:layer:caption": "Google.com"
              },
              "layout": {
                "visibility": "none"
              }
            },
            {
              id: 'fill',
              type: "fill",
              source: 'test',
              "source-layer": "layer",
              layout: {},
              paint: {
                'fill-color':["case",
                //Собственность физических лиц
                ["all",['==', ['get', 'right_type'], "1001000000"],["any",['==', ['get', 'sp'], "7003000000"],['==', ['get', 'sp'], "7003001000"],['==', ['get', 'sp'], "7003002000"]]],
						      "#8000FF",
                //Собственность юридических лиц
                ["all",['==', ['get', 'right_type'], "1001000000"],["any",['==', ['get', 'sp'], "7002000000"],['==', ['get', 'sp'], "7002001000"],['==', ['get', 'sp'], "7002002000"],['==', ['get', 'sp'], "7002003000"]]],
					      	"#00FF00", 
                //Собственность РФ
                ["all",['==', ['get', 'right_type'], "1001000000"],['==', ['get', 'sp'], "7001001001"]],
						      "#008000",
                //Собственность субъекта РФ
                ["all",['==', ['get', 'right_type'], "1001000000"],['==', ['get', 'sp'], "7001001002"]],
						      "#0080FF",
                //Собственность муниципального образования
                ["all",['==', ['get', 'right_type'], "1001000000"],['==', ['get', 'sp'], "7001001003"]],
						      "#FF8040",
                //Собственность сельских поселений
                ["all",['==', ['get', 'right_type'], "1001000000"],['==', ['get', 'sp'], "7001001099"]],
						      "#FF00FF",
                //Собственность РФ,суб.РФ,мун.обр.
                ["all",['==', ['get', 'right_type'], "1001000000"],["any",['==', ['get', 'sp'], "7001001000"],['==', ['get', 'sp'], "7001000000"]]],
                  "#150BE3", 
                //Иная собственность УТОЧНИТЬ 
                ["all",['==', ['get', 'right_type'], "1001000000"],["any",['==', ['get', 'sp'], null],['==', ['get', 'sp'], "7003003000"],['==', ['get', 'sp'], "7001002000"],['==', ['get', 'sp'], "7001003000"],['==', ['get', 'sp'], "7001004000"]]],
						      "#CC0000",                         
                //Общая долевая собственность
                ["any",['==', ['get', 'right_type'], "1002000000"],['==', ['get', 'right_type'], "1003000000"]],
                  "#FF8080",
                //Пожизненное наследуемое владение
                ['==', ['get', 'right_type'], "1006000000"],
                  "#804000",
                //Постоянное (бессрочное) пользование
                ['==', ['get', 'right_type'], "1007000000"],
                  "#808000",
                //Земельные участки, на которые не разграничен вид права
                ['==', ['get', 'right_type'], "1099000001"],
                  "#FFFF00",
                //Фонд перераспределения
                ['==', ['get', 'right_type'], "1099000002"],
                  "#FF0000",
                  "#333333"
						    ],
                "fill-opacity": 0.5,
                "fill-outline-color":["case",
                    ['==', ['get', 'cad_register_geom'], false], 
                    "red",
                    "black"
                ],
					  }
            },
            {
              id: 'polygon-label',
              type: "symbol",
              source: 'test',	
              "source-layer": "layer",
              minzoom: 13,
              layout: {
                'symbol-placement': "point",
                'text-field': ["get", "reg_num"],
                'text-size': 10,
              },
              paint: {
                'text-halo-width':2,
                'text-halo-color': 'white'
              }
            }
          ]
          },
          transformRequest: (url, resourceType) => {
            if (resourceType === 'Tile' && url.startsWith(this.$store.getters.value('host'))) {
              return {
                url: url,
                credentials: 'same-origin',
                headers: {
                  'Authorization': 'JWT ' + this.$cookies.get('access')
                }
              }
            }
          },
          center: [ 40.50668307108822, 50.93362216657363],
          zoom: 6.54
        })

        this.map.on('load', async e => {
          let res = await axios.get(this.$store.getters.value('ngwHost'))
          res.data.wmsserver_service.layers.forEach(lay => {
            if (lay.keyname === 'sp_bound') {
              this.map.addSource(lay.keyname, {
                type: 'raster',
                tiles: [this.$store.getters.value('ngwHost') + '/wms?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256&layers=' + lay.keyname],
                tileSize: 256
              })
              this.layersPopup.push({
                name: lay.display_name,
                id: lay.keyname,
                visibility: false
              })
              this.map.addLayer({
                id: lay.keyname,
                type: 'raster',
                source: lay.keyname,
                'metadata': {
                  'mapbox:layer:caption': lay.display_name
                },
                layout: {
                  'visibility': 'none'
                }
              })
            }
          })
          this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
              polygon: true,
              trash: true
            }
          })

          this.map.addControl(this.draw, 'top-left')
          this.map.on('draw.create', this.updateArea)
          this.map.on('draw.delete', this.updateArea)
          this.map.on('draw.update', this.updateArea)

          if (this.mode === 'edit' && this.initCoords !== null && this.initCoords !== undefined) { // если редактирование и етсь гуометрия - добавить фичу для редактирования
            this.initCoords.id = 'draw-edit'
            this.draw.add(this.initCoords)
            this.geometryForMap = this.draw.getAll()

            let bounds = turf.bbox(this.initCoords) // позиционировани на фичу // позиционировани на фичу
            this.map.fitBounds(bounds, {
              padding: 20
            })
          }
          this.mapLoad = false
        })

      })
    },

    updateArea (e) {
      var data = this.draw.getAll()
      data = this.updateDrawData(data, e)
      this.draw.deleteAll()
      this.draw.add(data)
      var answer = document.getElementById('calculated-area')
      if (data.features.length > 0) {
        var area = turf.area(data)
        // restrict to area to 2 decimal points
        this.roundedArea = Math.round(area * 100) / 100
        this.$emit('roundedArea', {
          areaBygeom: this.roundedArea
        })
      } else {
        answer.innerHTML = ''
        if (e.type !== 'draw.delete')
        alert('Use the draw tools to draw a polygon!')
      }
      this.geometryForMap = this.draw.getAll()
      this.$emit('setCoords', this.geometryForMap)
    },

    updateDrawData (data, e) { // ищет пересечения
      if (data.features.length < 2) {
        return data
      }
      let featureList = []
      if (e.type === 'draw.create') {
        let newFeature = e.features[0]
        let hasIntersect = false
        for (let i of data.features) { // проход по всем фичам
          if (i.id === newFeature.id) {
            continue
          }
          let intersect = turf.intersect(i, JSON.parse(JSON.stringify(newFeature)))
          if (intersect !== null) {
            hasIntersect = true
            let id = i.id
            let newPolygon = turf.difference(i, JSON.parse(JSON.stringify(newFeature)))
            i.id = id
            featureList.push(newPolygon)
          } else {
            featureList.push(i)
          }
        }
        if (!hasIntersect) { // если ни с какой из фич не пересекается, то добавить
          featureList.push(newFeature)
        }
      } else {
        return data
      }
      data.features = featureList
      return data
    }
  },
  mounted () {
    this.roundedArea = this.areaBygeomForMap
  }
}
</script>

<style lang="scss">

.layers-popup {
  position: absolute;
  padding: 5px 10px;
  top: 10px;
  left: 50px;
  border-radius: 8px;
  background-color: #fff;
  z-index: 2;
  span {
    cursor: pointer;
    &.active-layer {
      font-family: 'Montserrat_B';
      color: #4E53E7;
    }
  }
}

.create-edit-map-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  #popup-map {
    width: 100%;
    margin-top: 15px;
    height: calc(100% - 55px);
  }

  .minimap-preloader {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 3;
  }

  .axis {
    padding: 0;
    top: -50px;
    left: 0;
    display: flex;

    .v-select {
      width: 330px;
    }
    
    button {
      font-size: 14px;
      height: 38px;
      margin-left: 10px;
    }

    &-table {
      width: 100%;
    }
    &-row {
      width: 100%;
      border: 1px solid black;
    }
    .form-control {
      display: block;
    }
    &-inp {
      height: 90%;
      overflow-y: auto;
    }
    .form-control,
    &-cell {
      width: 50%;
      border: 1px solid black;

      input {
        padding: 0;
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/popup/MapCreateEditVue.vue